import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowRight } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";
import { CONTACT } from "../data/content";

const LINKS = [
    { id: "#leistungen", label: "Leistungen", testid: "nav-link-leistungen" },
    { id: "#vorher-nachher", label: "Vorher / Nachher", testid: "nav-link-vorher-nachher" },
    { id: "#warum-wir", label: "Warum wir", testid: "nav-link-warum-wir" },
    { id: "#ablauf", label: "Ablauf", testid: "nav-link-ablauf" },
    { id: "#region", label: "Einsatzgebiet", testid: "nav-link-region" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
                scrolled ? "bg-white/85 backdrop-blur-xl border-b border-forest/10 shadow-[0_1px_24px_rgba(11,18,14,0.06)]" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-[72px] flex items-center justify-between">
                <button
                    data-testid="brand-logo"
                    onClick={() => go("#top")}
                    className="relative flex items-center"
                    aria-label="Julians Gartenservice – zum Seitenanfang"
                >
                    <img src="/logo-light.png" alt="Julians Gartenservice" className={`h-10 w-auto transition-opacity duration-300 ${scrolled ? "opacity-0" : "opacity-100"}`} />
                    <img src="/logo-forest.png" alt="" aria-hidden="true" className={`absolute left-0 top-1/2 -translate-y-1/2 h-10 w-auto transition-opacity duration-300 ${scrolled ? "opacity-100" : "opacity-0"}`} />
                </button>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={l.testid}
                            onClick={() => go(l.id)}
                            className={`text-sm font-medium tracking-tight transition-colors duration-200 ${
                                scrolled ? "text-shade hover:text-forest" : "text-white/80 hover:text-white"
                            }`}
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        data-testid="header-phone-link"
                        href={CONTACT.phoneHref}
                        className={`hidden md:flex items-center gap-2 text-sm font-semibold transition-colors duration-200 ${
                            scrolled ? "text-forest hover:text-leaf" : "text-white hover:text-leaf-soft"
                        }`}
                    >
                        <Phone size={15} strokeWidth={2.4} />
                        {CONTACT.phoneDisplay}
                    </a>
                    <button
                        data-testid="header-cta-quote"
                        onClick={() => go("#angebot")}
                        className="hidden sm:flex items-center gap-2 bg-leaf hover:bg-forest text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-[background-color,transform] duration-200 hover:scale-[1.03] active:scale-[0.98]"
                    >
                        Angebot anfragen
                        <ArrowRight size={15} strokeWidth={2.4} />
                    </button>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        aria-label="Menü öffnen"
                        className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors ${scrolled ? "text-ink" : "text-white"}`}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden bg-white/97 backdrop-blur-xl border-b border-hairline shadow-xl"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`${l.testid}-mobile`}
                                    onClick={() => go(l.id)}
                                    className="text-left text-lg font-display font-medium text-ink py-3 border-b border-hairline last:border-0"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="mobile-menu-cta-quote"
                                onClick={() => go("#angebot")}
                                className="mt-4 flex items-center justify-center gap-2 bg-leaf text-white font-semibold px-5 py-3.5 rounded-full"
                            >
                                Kostenloses Angebot anfragen
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
